import React, {FC, memo, useState, useMemo, useEffect} from "react";
import {Table, Radio, Input, Button, Space, Tooltip, Tag} from "antd";
import {RollbackOutlined} from "@ant-design/icons";
import {ColumnsType} from "antd/lib/table";
import moment from "moment";
import i18n from "../../services/i18n";
import {PaymentProvider} from "../../generated/graphql";
import {useCurrency} from "../../helpers/currencyHelper";
import css from "./RefundPanel.less";
import RefundAmountFields from "./RefundAmountFields";

// Phase 14 / D-14: Wiederverwendbare Refund-Panel-View mit 3 Modi
//  - "single"   → Standalone-Button-Flow (Plan 14-05); rendert eigenen Footer
//  - "storno"   → Inline in ReverseBookingModal; Parent steuert Submit via onSelectionChange
//  - "umbuchen" → Inline in TransferAttendeeToAnotherCourse; wie storno + Transfer-Option

export type RefundMode = "single" | "storno" | "umbuchen";

export type RefundChoice = "full" | "partial" | "none" | "transfer";

export type RefundPanelRow = {
    scheduledPaymentId: string;
    paymentAmount: number;
    refundedAmount: number;
    paymentDue: Date | string;
    paymentDone: Date | string | null;
    title: string | null;
    paymentProvider: PaymentProvider;
    mollieMethod?: string | null;
    // Bug-Fix Mollie-Refund-Cap: tatsächlich bei Mollie gezahlter Betrag.
    // Kann kleiner sein als paymentAmount (z.B. nachträgliche Discount-Änderung) —
    // ein Refund darf NIE mehr als diesen Betrag anbieten, sonst Mollie 422.
    // null/undefined → Fallback auf paymentAmount (bisheriges Verhalten).
    molliePaidAmount?: number | null;
};

type RefundSelection = {
    choice: RefundChoice;
    partialAmount?: number | null;
};

type RefundOutput = Array<{scheduledPaymentId: string; amount: number; kind: RefundChoice}>;

export type RefundPanelProps = {
    scheduledPayments: RefundPanelRow[];
    mode: RefundMode;
    targetBookingId?: string;
    onConfirm?: (params: {refunds: RefundOutput; reason: string}) => Promise<void>;
    onCancel?: () => void;
    /** complete: für jede erstattbare Rate ist entschieden (und ein Teilbetrag gültig). */
    onSelectionChange?: (params: {refunds: RefundOutput; reason: string; complete: boolean}) => void;
    loading?: boolean;
};

// Bug-Fix Mollie-Refund-Cap: Maximal erstattbarer Betrag einer Rate.
// Basis ist der TATSÄCHLICH bei Mollie gezahlte Betrag (molliePaidAmount),
// falls vorhanden — sonst der lokale paymentAmount (bisheriges Verhalten).
// Davon wird der bereits refundierte Betrag abgezogen.
// Muss VOR isMollieRefundable deklariert sein (arrow-const ist nicht gehoistet).
export const getRefundCap = (row: RefundPanelRow): number => {
    const base = row.molliePaidAmount != null ? Number(row.molliePaidAmount) : Number(row.paymentAmount);
    return base - Number(row.refundedAmount || 0);
};

// D-15 / D-12: refund-fähig nur wenn Mollie + bezahlt + Cap > 0.
// Bug-Fix Mollie-Refund-Cap: Statt der naiven Bedingung `refundedAmount < paymentAmount`
// wird der echte Cap (getRefundCap) geprüft. Sonst entsteht eine "Ghost-Row": Eine Rate
// mit molliePaidAmount < paymentAmount und bereits voll erstattet (Cap = 0) würde als
// wählbar mit Default "Voll-Refund" angezeigt, der aggregierte Output bliebe aber leer
// (amount > 0 greift nicht) → kein Refund-Request, kein Feedback für den Nutzer.
export const isMollieRefundable = (row: RefundPanelRow) =>
    row.paymentProvider === PaymentProvider.Mollie
    && row.paymentDone !== null
    && row.paymentDone !== undefined
    && getRefundCap(row) > 0;

// True, wenn bei Mollie real weniger gezahlt wurde als lokal als paymentAmount
// hinterlegt ist → der Hinweis-Tag in der Betrags-Spalte wird gezeigt.
export const isMolliePaidAmountReduced = (row: RefundPanelRow): boolean =>
    row.molliePaidAmount != null && Number(row.molliePaidAmount) < Number(row.paymentAmount);

const RefundPanel: FC<RefundPanelProps> = ({
    scheduledPayments,
    mode,
    onConfirm,
    onCancel,
    onSelectionChange,
    loading,
}) => {
    // Tenant-Währung: das Betrags-Zahlenformat (toFixed(2)) bleibt unverändert,
    // nur das Symbol richtet sich nach der Organisation (€/CHF/…).
    const currency = useCurrency();
    // Default-Auswahl je Modus:
    //  - umbuchen → "transfer" (Guthaben auf neue Buchung übertragen, KEINE Rückzahlung
    //    an den Kunden). Das ist der sichere Normalfall beim Umbuchen; Voll-/Teil-Refund
    //    sind die bewusste Ausnahme. Früher war auch hier "full" der Default — das löste
    //    standardmäßig einen Refund aus (und führte mit der alten Übertrags-Logik zur
    //    Doppel-Gutschrift).
    //  - storno/single → KEINE Vorauswahl. Früher war "full" vorausgewählt; wer eine
    //    Stornogebühr nur ins Grund-Feld schrieb, erstattete trotzdem alles (Nübad, 08.10.2026).
    const [selections, setSelections] = useState<Record<string, RefundSelection>>(() => {
        const init: Record<string, RefundSelection> = {};
        if (mode === "umbuchen") {
            scheduledPayments.forEach((sp) => {
                if (isMollieRefundable(sp)) {
                    init[sp.scheduledPaymentId] = {choice: "transfer"};
                }
            });
        }
        return init;
    });
    const [reason, setReason] = useState<string>("");

    // Radio-Optionen je Modus.
    //  - umbuchen: "Guthaben auf neue Buchung übertragen" (kein Refund, Default) +
    //    Voll-/Teil-Refund (zahlen bewusst an den Kunden zurück, neue Buchung dann
    //    ohne dieses Guthaben). "Kein Refund" entfällt — es wäre funktional identisch
    //    zu "Guthaben übertragen" und war die Quelle der früheren Verwirrung.
    //  - storno/single: Voll-/Teil-Refund + Kein Refund (kein Transfer-Kontext).
    const radioOptions = useMemo<Array<{value: RefundChoice; label: string}>>(() => {
        if (mode === "umbuchen") {
            return [
                {value: "transfer", label: i18n.views.RefundPanel.radioTransfer()},
                {value: "full", label: i18n.views.RefundPanel.radioFull()},
                {value: "partial", label: i18n.views.RefundPanel.radioPartial()},
            ];
        }
        return [
            {value: "full", label: i18n.views.RefundPanel.radioFull()},
            {value: "partial", label: i18n.views.RefundPanel.radioPartial()},
            {value: "none", label: i18n.views.RefundPanel.radioNone()},
        ];
    }, [mode]);

    // Aggregiere aktuelle Auswahl in Refund-Output-Shape
    const aggregatedOutput: RefundOutput = useMemo(() => {
        const refunds: RefundOutput = [];
        scheduledPayments.forEach((sp) => {
            if (!isMollieRefundable(sp)) return;
            const sel = selections[sp.scheduledPaymentId];
            if (!sel || sel.choice === "none") return;

            // Cap auf real bei Mollie gezahlten Betrag (sonst Mollie 422 bei Refund)
            const refundCap = getRefundCap(sp);
            let amount: number;
            if (sel.choice === "partial") {
                // Teilbetrag zusätzlich hart auf den Cap deckeln (Schutz, falls
                // der eingegebene Wert den Cap übersteigt)
                amount = Math.min(Number(sel.partialAmount || 0), refundCap);
            } else {
                // full oder transfer → voller (gedeckelter) Restbetrag
                amount = refundCap;
            }
            if (amount > 0) {
                refunds.push({scheduledPaymentId: sp.scheduledPaymentId, amount, kind: sel.choice});
            }
        });
        return refunds;
    }, [selections, scheduledPayments]);

    const totalAmount = useMemo(
        () => aggregatedOutput.reduce((sum, r) => sum + r.amount, 0),
        [aggregatedOutput],
    );

    // Vollständig entschieden: jede erstattbare Rate hat eine Wahl, Teilbeträge liegen in (0, Restbetrag].
    const complete = useMemo(
        () => scheduledPayments.every((sp) => {
            if (!isMollieRefundable(sp)) return true;
            const sel = selections[sp.scheduledPaymentId];
            if (!sel) return false;
            if (sel.choice !== "partial") return true;
            const amount = sel.partialAmount;
            return amount != null && amount > 0 && amount <= getRefundCap(sp) + 0.001;
        }),
        [selections, scheduledPayments],
    );

    // Umbuchen-Modus: Aufteilung des bei Mollie gezahlten Betrags in
    //  (1) "Erstattung an Kunde"   = Summe der full/partial-Refunds (echte Rückzahlung)
    //  (2) "Übertrag auf neue Buchung" = Restbetrag, der als Guthaben mitgeht
    // Beides zusammen ergibt den gesamten erstattbaren Betrag (totalAvailable). Diese
    // Anzeige ist deckungsgleich mit dem Backend (getPaidAmountToTransfer zieht den
    // erstatteten Betrag jetzt ab), sodass der Nutzer den finanziellen Effekt VOR dem
    // Bestätigen sieht.
    const totalAvailable = useMemo(
        () => scheduledPayments.reduce((sum, sp) => (isMollieRefundable(sp) ? sum + getRefundCap(sp) : sum), 0),
        [scheduledPayments],
    );
    const refundToCustomerTotal = useMemo(
        () => aggregatedOutput.reduce((sum, r) => (r.kind === "transfer" ? sum : sum + r.amount), 0),
        [aggregatedOutput],
    );
    const transferToBookingTotal = Math.max(0, totalAvailable - refundToCustomerTotal);

    // D-07: In mode=storno/umbuchen wird Parent bei jeder Änderung benachrichtigt —
    // der Submit läuft dann über den bestehenden Modal-Confirm-Button des umschließenden Forms.
    useEffect(() => {
        if (mode !== "single" && onSelectionChange) {
            onSelectionChange({refunds: aggregatedOutput, reason, complete});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregatedOutput, reason, mode, complete]);

    const setChoice = (spId: string, choice: RefundChoice) =>
        setSelections((prev) => ({...prev, [spId]: {choice, partialAmount: prev[spId]?.partialAmount}}));
    const setPartial = (spId: string, amount: number | null) =>
        setSelections((prev) => ({...prev, [spId]: {choice: prev[spId]?.choice ?? "partial", partialAmount: amount}}));

    const columns: ColumnsType<RefundPanelRow> = [
        {
            title: i18n.views.RefundPanel.columnDue(),
            dataIndex: "paymentDue",
            render: (v: Date | string) => (v ? moment(v).format("DD.MM.YYYY") : "—"),
        },
        {
            title: i18n.views.RefundPanel.columnAmount(),
            dataIndex: "paymentAmount",
            align: "right",
            render: (_v: unknown, row: RefundPanelRow) => {
                if (isMolliePaidAmountReduced(row)) {
                    const paidFormatted = `${Number(row.molliePaidAmount).toFixed(2)} ${currency.symbol}`;
                    return (
                        <Space size={4}>
                            <span>{`${Number(row.paymentAmount).toFixed(2)} ${currency.symbol}`}</span>
                            <Tooltip title={i18n.views.RefundPanel.molliePaidAmountHint({amount: paidFormatted})}>
                                <Tag color="orange" data-testid="molliePaidAmountReducedTag">
                                    {i18n.views.RefundPanel.molliePaidAmountBadge({amount: paidFormatted})}
                                </Tag>
                            </Tooltip>
                        </Space>
                    );
                }
                const amountText = `${Number(row.paymentAmount).toFixed(2)} ${currency.symbol}`;
                // Bezugsbasis sichtbar machen: Erstatten/Einbehalten beziehen sich auf den Restbetrag.
                if (Number(row.refundedAmount || 0) > 0) {
                    return (
                        <>
                            <div>{amountText}</div>
                            <div style={{fontSize: 12, color: "rgba(0, 0, 0, 0.45)"}}>
                                {i18n.views.RefundPanel.alreadyRefunded({amount: `${Number(row.refundedAmount).toFixed(2)} ${currency.symbol}`})}
                            </div>
                        </>
                    );
                }
                return amountText;
            },
        },
        {
            title: i18n.views.RefundPanel.columnPaidAt(),
            dataIndex: "paymentDone",
            render: (v: Date | string | null) => (v ? moment(v).format("DD.MM.YYYY") : "—"),
        },
        {
            title: i18n.views.RefundPanel.columnRefundChoice(),
            render: (_v: unknown, row: RefundPanelRow) => {
                if (!isMollieRefundable(row)) {
                    return (
                        <Tooltip title={i18n.views.RefundPanel.manualHint()}>
                            <Tag color="default">{i18n.views.RefundPanel.manualBadge()}</Tag>
                        </Tooltip>
                    );
                }
                const sel = selections[row.scheduledPaymentId];
                return (
                    <Radio.Group
                        value={sel?.choice}
                        onChange={(e) => setChoice(row.scheduledPaymentId, e.target.value as RefundChoice)}
                    >
                        {radioOptions.map((opt) => (
                            <Radio key={opt.value} value={opt.value}>
                                {opt.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                );
            },
        },
        {
            title: i18n.views.RefundPanel.columnPartialAmount(),
            render: (_v: unknown, row: RefundPanelRow) => {
                if (!isMollieRefundable(row)) return null;
                const sel = selections[row.scheduledPaymentId];
                if (sel?.choice !== "partial") return null;
                return (
                    <div style={{width: 240}}>
                        {/* Cap auf real bei Mollie gezahlten Betrag (sonst Mollie 422 bei Refund) */}
                        <RefundAmountFields
                            size="small"
                            remaining={getRefundCap(row)}
                            refundAmount={sel.partialAmount ?? null}
                            onChange={(amount) => setPartial(row.scheduledPaymentId, amount)}
                        />
                    </div>
                );
            },
        },
    ];

    const handleFooterConfirm = async () => {
        if (onConfirm) {
            await onConfirm({refunds: aggregatedOutput, reason});
        }
    };

    return (
        <div className={css.refundPanel}>
            <div className={css.refundPanelTitle}>{i18n.views.RefundPanel.title()}</div>
            <Table<RefundPanelRow>
                columns={columns}
                dataSource={scheduledPayments}
                rowKey="scheduledPaymentId"
                pagination={false}
                size="small"
                rowClassName={(row) => (!isMollieRefundable(row) ? css.manualRow : "")}
            />
            {mode === "umbuchen" ? (
                <>
                    <div className={css.summary}>
                        <span className={css.summaryLabel}>{i18n.views.RefundPanel.summaryRefundToCustomer()}</span>
                        <span className={css.summaryAmount}>{`${refundToCustomerTotal.toFixed(2)} ${currency.symbol}`}</span>
                    </div>
                    <div className={css.summary}>
                        <span className={css.summaryLabel}>{i18n.views.RefundPanel.summaryTransferToBooking()}</span>
                        <span className={css.summaryAmount}>{`${transferToBookingTotal.toFixed(2)} ${currency.symbol}`}</span>
                    </div>
                </>
            ) : (
                <div className={css.summary}>
                    <span className={css.summaryLabel}>{i18n.views.RefundPanel.summaryTotal()}</span>
                    <span className={css.summaryAmount}>{`${totalAmount.toFixed(2)} ${currency.symbol}`}</span>
                    {/* Beim Storno ist alles Nicht-Erstattete einbehalten (z. B. Stornogebühr). */}
                    {mode === "storno" && complete && (
                        <>
                            <span className={css.summaryLabel}>{i18n.views.RefundPanel.summaryRetained()}</span>
                            <span className={css.summaryAmount} data-testid="refund-panel-retained">
                                {`${Math.max(0, totalAvailable - totalAmount).toFixed(2)} ${currency.symbol}`}
                            </span>
                        </>
                    )}
                </div>
            )}
            {mode !== "umbuchen" && !complete && (
                <div className={css.choiceHint}>{i18n.views.RefundPanel.choiceRequired()}</div>
            )}
            <div className={css.reasonWrapper}>
                <Input.TextArea
                    placeholder={i18n.views.RefundPanel.reasonPlaceholder()}
                    maxLength={500}
                    showCount
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                />
            </div>
            {mode === "single" && (
                <div className={css.footerActions}>
                    <Space>
                        {onCancel && (
                            <Button onClick={onCancel}>{i18n.views.RefundPanel.buttonCancel()}</Button>
                        )}
                        <Button
                            type="primary"
                            icon={<RollbackOutlined />}
                            loading={loading}
                            disabled={!complete || aggregatedOutput.length === 0}
                            onClick={handleFooterConfirm}
                        >
                            {i18n.views.RefundPanel.buttonConfirm()}
                        </Button>
                    </Space>
                </div>
            )}
        </div>
    );
};

export default memo(RefundPanel);
